import { reactive } from 'vue';
import {
  getOrderExportStatus,
  getOrderExportData,
  cancelOrderExport,
  deleteExportFile,
} from 'src/js/api';

const KEY = 'crm:orderExportState';
const POLL_MS = 2000;

export const exportState = reactive({
  taskId: null,
  progress: 0,
  total: 0,
  readyFile: null,
});

let handle = null;
let inFlight = false;

const save = () => {
  try {
    localStorage.setItem(KEY, JSON.stringify({
      taskId: exportState.taskId,
      total: exportState.total,
      readyFile: exportState.readyFile,
    }));
  } catch (e) {
    console.warn('[export] save failed:', e?.message || e);
  }
};

const reset = () => {
  exportState.taskId = null;
  exportState.progress = 0;
  exportState.total = 0;
  exportState.readyFile = null;
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    console.warn('[export] clear failed:', e?.message || e);
  }
};

const stop = () => {
  if (handle) { clearInterval(handle); handle = null; }
};

export const triggerDownload = () => {
  const file = exportState.readyFile;
  if (!file?.fileId) return;
  const token = localStorage.getItem('crm_auth_token');
  const a = document.createElement('a');
  a.href = `${process.env.CRM_URL}assets/${file.fileId}?download&access_token=${token}`;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const autoDownloadOnce = () => {
  const file = exportState.readyFile;
  if (!file?.fileId || file.autoDownloadTriggered) return;
  triggerDownload();
  exportState.readyFile = { ...file, autoDownloadTriggered: true };
  save();
};

const pollOnce = async (taskId) => {

  if (inFlight) return;

  if (exportState.readyFile?.fileId) return;
  inFlight = true;
  try {
    const status = await getOrderExportStatus(taskId);
    if (status?.status === 'processing' && status.processed > 0) {
      exportState.progress = status.processed;
    }
    if (status?.status === 'done') {
      stop();
      const data = await getOrderExportData(taskId);
      if (!data?.fileId) {
        if (exportState.readyFile?.fileId) return;
        console.warn('[export] /data returned no fileId — keeping state, will retry on next tick');
        return;
      }
      exportState.readyFile = {
        fileId: data.fileId,
        filename: data.filename || 'bestellung-export.csv',
        processed: data.processed,
        createdAt: Date.now(),
        autoDownloadTriggered: false,
      };
      exportState.taskId = null;
      exportState.progress = 0;
      save();
      autoDownloadOnce();
    } else if (status?.status === 'failed') {
      stop();
      reset();
    }
  } catch (e) {
    console.warn('[export] poll failed:', e?.message || e);
  } finally {
    inFlight = false;
  }
};

export const startExportPolling = (taskId, total = 0) => {
  stop();
  Object.assign(exportState, { taskId, total, progress: 0, readyFile: null });
  save();
  handle = setInterval(() => pollOnce(taskId), POLL_MS);
};

export const cancelExportPolling = async () => {
  const taskId = exportState.taskId;
  stop();
  reset();
  if (!taskId) return;
  try {
    await cancelOrderExport(taskId);
  } catch (e) {
    console.warn('[export] cancel failed:', e?.message || e);
  }
};

export const dismissExportFile = async () => {
  const fileId = exportState.readyFile?.fileId;
  reset();
  if (fileId) await deleteExportFile(fileId);
};

export const restoreExportPolling = () => {
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { /* corrupt */ }
  if (!saved) return;
  if (saved.readyFile?.fileId) {
    exportState.readyFile = saved.readyFile;
    exportState.total = saved.total || 0;
    if (!saved.readyFile.autoDownloadTriggered) setTimeout(autoDownloadOnce, 0);
  } else if (saved.taskId) {
    startExportPolling(saved.taskId, saved.total || 0);
  }
};
